import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Modal, ModalBody } from 'reactstrap'
import { ICONS } from '../OnboardingComponents/icon'

const HOW_TO_STEPS = [
  {
    title: 'Add Basic Details',
    body: 'Enter a task title and select the start and end dates for your schedule.',
  },
  {
    title: 'Select Working Days',
    body: 'Choose the days of the week you typically work. You can select one or more days. Only selected days will appear below.',
    bullets: [
      'You can select one or more days.',
      'Only selected days will appear below.',
    ],
  },
  {
    title: 'Assign Working Hours',
    body: 'For each selected day, set your start time and end time. Click the time fields to pick from the time picker. End time must be later than start time.',
    bullets: [
      'Click the time fields to pick from the time picker.',
      'End time must be later than start time.',
    ],
  },
  {
    title: 'Add Another Day (Optional)',
    body: "Click '+ Add Another Day' to include more days in your schedule.",
  },
  {
    title: 'Save Your Schedule',
    body: "Review your schedule and click 'Save Schedule'. The schedule will repeat every week within the selected date range.",
  },
]

const HOW_TO_NOTES = [
  'You must select at least one working day.',
  'Start time must be earlier than end time.',
  'Schedule will be active between Start Date and End Date.',
  'You can edit or update your schedule anytime.',
]

/**
 * ProjectCreatedModal
 * ------------------------------------------------------------------
 * Success modal shown right after a project is created. Gives the user
 * a clear next step: set up a calendar, create a task, or skip.
 */
const ProjectCreatedModal = ({
  isOpen,
  onClose,
  onSetupCalendar,
  onCreateTask,
  onSkip,
}) => {
  const [isTipOpen, setIsTipOpen] = useState(false)

  useEffect(() => {
    if (!isOpen) setIsTipOpen(false)
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      className={`amok-modal amok-modal--created${
        isTipOpen ? ' amok-modal--created-tip-open' : ''
      }`}
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body">
        <div className="amok-modal__success">
          <div className="amok-modal__check" aria-hidden="true">
            <span className="amok-modal__check-inner">
              <img
                src={ICONS.tickWhite}
                alt=""
                className="amok-modal__check-tick"
              />
            </span>
          </div>
          <h2 className="amok-modal__title">Project Created!</h2>
          <p className="amok-modal__subtitle">What would you like to do next?</p>
        </div>

        <div className={`amok-modal__tip ${isTipOpen ? 'amok-modal__tip--open' : ''}`}>
          <button
            type="button"
            className="amok-modal__tip-header"
            onClick={() => setIsTipOpen((s) => !s)}
            aria-expanded={isTipOpen}
            aria-controls="amok-modal-howto-body"
          >
            <span className="amok-modal__tip-icon">
              <img src={ICONS.information} alt="" />
            </span>
            <span className="amok-modal__tip-text">
              <span className="amok-modal__tip-title">How to Use</span>
              <span className="amok-modal__tip-desc">
                You can create your working schedule in just a few simple steps.
              </span>
            </span>
            <img
              src={ICONS.chevronDownBlue}
              alt=""
              className={`amok-modal__tip-chevron ${
                isTipOpen ? 'amok-modal__tip-chevron--open' : ''
              }`}
              aria-hidden="true"
            />
          </button>
          {isTipOpen && (
            <div
              id="amok-modal-howto-body"
              className="amok-modal__tip-body"
              role="region"
              aria-label="How to use steps"
            >
              <div className="amok-modal__tip-scroll">
                {HOW_TO_STEPS.map((step) => (
                  <div className="amok-modal__tip-step" key={step.title}>
                    <p className="amok-modal__tip-step-title">{step.title}</p>
                    <p className="amok-modal__tip-step-body">{step.body}</p>
                    {step.bullets && (
                      <ul className="amok-modal__tip-bullets">
                        {step.bullets.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
                <div className="amok-modal__tip-note">
                  <p className="amok-modal__tip-note-title">Note</p>
                  <ul className="amok-modal__tip-bullets amok-modal__tip-bullets--note">
                    {HOW_TO_NOTES.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="amok-modal__actions">
          <button
            type="button"
            className="amok-modal__action amok-modal__action--calendar"
            onClick={onSetupCalendar}
          >
            <span className="amok-modal__action-icon-wrap">
              <img
                src={ICONS.calendar}
                alt=""
                className="amok-modal__action-img"
              />
            </span>
            <span className="amok-modal__action-content">
              <span className="amok-modal__action-title">Set up Calendar</span>
              <span className="amok-modal__action-desc">
                Schedule milestones and deadlines
              </span>
            </span>
            <img
              src={ICONS.arrowRight}
              alt=""
              className="amok-modal__action-arrow-img"
            />
          </button>

          <button
            type="button"
            className="amok-modal__action amok-modal__action--task"
            onClick={onCreateTask}
          >
            <span className="amok-modal__action-icon-wrap">
              <img
                src={ICONS.listCheck}
                alt=""
                className="amok-modal__action-img amok-modal__action-img--list"
              />
            </span>
            <span className="amok-modal__action-content">
              <span className="amok-modal__action-title">Create Task</span>
              <span className="amok-modal__action-desc">
                Break your project into actionable steps
              </span>
            </span>
            <img
              src={ICONS.arrowRight}
              alt=""
              className="amok-modal__action-arrow-img amok-modal__action-arrow-img--green"
            />
          </button>
        </div>

        {onSkip && (
          <button type="button" className="amok-modal__skip" onClick={onSkip}>
            Skip for now
          </button>
        )}
      </ModalBody>
    </Modal>
  )
}

ProjectCreatedModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSetupCalendar: PropTypes.func,
  onCreateTask: PropTypes.func,
  onSkip: PropTypes.func,
}

export default ProjectCreatedModal
